import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Plus, Minus, Check, RefreshCw } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import SubscribeDialog from './SubscribeDialog';

const nameToSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const ProductCard = ({ product }) => {
  const { cart, addToCart, updateCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const slug = nameToSlug(product.name);
  const [quantity, setQuantity] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);

  useEffect(() => {
    // Check if product is in cart and set quantity
    if (cart?.items) {
      const cartItem = cart.items.find(item => item.product_id === product.id);
      setQuantity(cartItem ? cartItem.quantity : 0);
    }
  }, [cart, product.id]);

  const handleAddToCart = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setIsUpdating(true);
    try {
      await addToCart(product.id, 1);
    } catch (error) {
      console.error('Failed to add to cart:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSubscribe = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setShowSubscribeDialog(true);
  };

  const handleIncrement = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setIsUpdating(true);
    try {
      await updateCart(product.id, quantity + 1);
    } catch (error) {
      console.error('Failed to update cart:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDecrement = async () => {
    if (!user) return;

    setIsUpdating(true);
    try {
      await updateCart(product.id, quantity - 1);
    } catch (error) {
      console.error('Failed to update cart:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card className="group relative bg-white rounded-2xl overflow-hidden border border-amber-50 shadow-sm hover:shadow-xl transition-all duration-300" data-testid={`product-card-${product.id}`}>
      <Link to={`/products/${slug}`}>
        <div className="aspect-square overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            data-testid={`product-image-${product.id}`}
          />
        </div>
      </Link>
      
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <Link to={`/products/${slug}`}>
              <h3 className="font-semibold text-accent group-hover:text-primary transition-colors line-clamp-2" data-testid={`product-name-${product.id}`}>
                {product.name}
              </h3>
            </Link>
            {product.weight && (
              <p className="text-xs text-muted-foreground mt-1">{product.weight}</p>
            )}
          </div>
          <Badge className="bg-success text-black text-xs" data-testid="eggless-badge">
            Eggless
          </Badge>
        </div>
        
        <div className="flex items-center justify-between mt-3">
          <span className="text-xl font-bold text-primary" data-testid={`product-price-${product.id}`}>
            ₹{product.price}
          </span>
          
          {quantity === 0 ? (
            <Button
              size="sm"
              className="rounded-full bg-primary hover:bg-primary/90 hover:scale-105 active:scale-95 transition-all text-xs sm:text-sm px-3 sm:px-4"
              onClick={handleAddToCart}
              disabled={isUpdating}
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Add
            </Button>
          ) : (
            <div className="flex items-center gap-1 sm:gap-2 bg-primary/10 rounded-full px-1 sm:px-2 py-1" data-testid={`quantity-controls-${product.id}`}>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 sm:h-7 sm:w-7 rounded-full hover:bg-primary hover:text-white"
                onClick={handleDecrement}
                disabled={isUpdating}
                data-testid={`decrease-${product.id}`}
              >
                <Minus className="h-3 w-3" />
              </Button>
              <div className="flex items-center gap-0.5 sm:gap-1 min-w-[30px] sm:min-w-[40px] justify-center">
                <Check className="h-3 w-3 text-success hidden sm:block" />
                <span className="font-bold text-accent text-xs sm:text-sm" data-testid={`quantity-${product.id}`}>
                  {quantity}
                </span>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 sm:h-7 sm:w-7 rounded-full hover:bg-primary hover:text-white"
                onClick={handleIncrement}
                disabled={isUpdating}
                data-testid={`increase-${product.id}`}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
        
        {/* Subscribe Button - Full width on separate row for mobile visibility */}
        <Button
          size="sm"
          variant="outline"
          className="w-full mt-2 h-8 text-xs border-primary/30 text-primary hover:text-primary hover:bg-primary/10"
          onClick={handleSubscribe}
          data-testid={`subscribe-${product.id}`}
        >
          <RefreshCw className="h-3 w-3 mr-1.5" />
          Subscribe
        </Button>
      </div>

      {/* Subscribe Dialog */}
      <SubscribeDialog
        product={product}
        open={showSubscribeDialog}
        onOpenChange={setShowSubscribeDialog}
        onSuccess={() => navigate('/subscriptions')}
      />
    </Card>
  );
};
